import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { DialogClose, DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  useSetVisibilityBulk,
  VISIBILITY_BATCH_MAX,
  type VisibilityOutcome,
  type YoutubePrivacy,
} from '@/api/endpoints/videos'
import { errorMessage, isApiError, isYoutubeQuotaError, isYoutubeReauthError } from '@/lib/errors'
import styles from './VisibilityDialog.module.scss'

/** Os rótulos do ``YoutubeUploadModal``, na ordem da troca. */
export const PRIVACY_LABEL: Record<YoutubePrivacy, string> = {
  public: 'Público',
  unlisted: 'Não listado',
  private: 'Privado',
}

const OPTIONS: ReadonlyArray<SegmentedOption<YoutubePrivacy>> = [
  { value: 'public', label: PRIVACY_LABEL.public },
  { value: 'unlisted', label: PRIVACY_LABEL.unlisted },
  { value: 'private', label: PRIVACY_LABEL.private },
]

export interface VisibilityDialogItem {
  videoId: string
  lang: string
  youtubeId: string
  title: string
  /** Visibilidade atual no YouTube; null quando ainda não lida. */
  privacy: YoutubePrivacy | null
}

export interface VisibilityDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Os vídeos que a troca alcança (publicados, um por id do YouTube). */
  items: readonly VisibilityDialogItem[]
  /** Selecionados que ficam de fora, para a linha de contagem. */
  scheduled?: number
  unpublished?: number
}

function countLabel(n: number, singular: string, plural: string): string {
  return `${String(n)} ${n === 1 ? singular : plural}`
}

/**
 * A mensagem de uma falha por item. Os 429 do YouTube (cota, "espere") e o
 * 401 de reconectar trazem o texto do backend, que diz o que fazer; o resto
 * vai pelo ``errorMessage`` de sempre.
 */
export function visibilityErrorMessage(err: unknown): string {
  if (isApiError(err) && (err.status === 429 || isYoutubeReauthError(err)) && err.problem?.detail) {
    return err.problem.detail
  }
  return errorMessage(err)
}

/** Sugere o estado oposto ao de todos, quando todos estão no mesmo. */
function initialPrivacy(items: readonly VisibilityDialogItem[]): YoutubePrivacy {
  const first = items[0]?.privacy
  return first === 'public' && items.every((i) => i.privacy === 'public') ? 'unlisted' : 'public'
}

/**
 * Troca a visibilidade no YouTube de um ou mais vídeos publicados: da barra de
 * seleção (vários) e da aba YouTube do projeto (um). O laço é sequencial e
 * para na cota (``runVisibilityBatch``); o resultado de cada item aparece
 * aqui conforme termina, e a dialog não fecha enquanto o laço roda.
 */
export function VisibilityDialog({ open, onOpenChange, items, scheduled = 0, unpublished = 0 }: VisibilityDialogProps) {
  const mutation = useSetVisibilityBulk()
  const [privacy, setPrivacy] = useState<YoutubePrivacy>(() => initialPrivacy(items))
  const [results, setResults] = useState<ReadonlyMap<string, VisibilityOutcome>>(new Map())

  // Cada abertura começa do zero: sem resultado da vez anterior.
  useEffect(() => {
    if (!open) return
    setResults(new Map())
    setPrivacy(initialPrivacy(items))
    mutation.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só na abertura
  }, [open])

  const running = mutation.isPending
  const finished = !running && results.size > 0
  const overLimit = items.length > VISIBILITY_BATCH_MAX
  const leftOut = scheduled + unpublished

  function confirm() {
    setResults(new Map())
    mutation.mutate(
      {
        targets: items.map((i) => ({ videoId: i.videoId, lang: i.lang, youtubeId: i.youtubeId })),
        privacy,
        onProgress: (outcome) => {
          setResults((prev) => new Map(prev).set(outcome.target.youtubeId, outcome))
        },
      },
      {
        onSuccess: (outcomes) => {
          summaryToast(outcomes)
        },
      },
    )
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!running) onOpenChange(next)
      }}
    >
      <DialogContent
        title={items.length === 1 ? 'Mudar a visibilidade no YouTube' : `Mudar a visibilidade de ${countLabel(items.length, 'vídeo', 'vídeos')}`}
        description="Muda só no YouTube. O Instagram não muda; post do Instagram ainda não publicado é cancelado se o vídeo deixar de ser público."
        hideClose={running}
        footer={
          finished ? (
            <DialogClose asChild>
              <Button variant="secondary">Fechar</Button>
            </DialogClose>
          ) : (
            <>
              <DialogClose asChild>
                <Button variant="secondary" disabled={running}>
                  Cancelar
                </Button>
              </DialogClose>
              <Button onClick={confirm} disabled={running || items.length === 0 || overLimit}>
                {running
                  ? `Mudando ${String(results.size + 1)} de ${String(items.length)}...`
                  : overLimit
                    ? `Até ${String(VISIBILITY_BATCH_MAX)} por vez (${String(items.length)} selecionados)`
                    : `Mudar para ${PRIVACY_LABEL[privacy]}`}
              </Button>
            </>
          )
        }
      >
        <div className={styles.field}>
          <Segmented<YoutubePrivacy>
            options={OPTIONS}
            value={privacy}
            onValueChange={(v) => {
              if (!running) setPrivacy(v)
            }}
            aria-label="Visibilidade"
          />
          {items.length > 1 ? (
            <span className={styles.limit}>até {VISIBILITY_BATCH_MAX} por vez</span>
          ) : null}
        </div>
        {leftOut > 0 ? (
          <p className={styles.leftOut}>
            {countLabel(scheduled, 'agendado', 'agendados')} e {countLabel(unpublished, 'não publicado', 'não publicados')}{' '}
            {leftOut === 1 ? 'fica' : 'ficam'} de fora.
          </p>
        ) : null}
        <ul className={styles.list}>
          {items.map((item) => (
            <VisibilityRow key={item.youtubeId} item={item} outcome={results.get(item.youtubeId)} />
          ))}
        </ul>
        {mutation.isError ? <p className={styles.error}>{errorMessage(mutation.error)}</p> : null}
      </DialogContent>
    </DialogRoot>
  )
}

function VisibilityRow({ item, outcome }: { item: VisibilityDialogItem; outcome: VisibilityOutcome | undefined }) {
  const current = outcome && outcome.state !== 'failed' && outcome.state !== 'skipped' ? outcome.result.current : item.privacy
  return (
    <li className={styles.item}>
      <span className={styles.itemTitle}>{item.title}</span>
      <span className={styles.itemState}>
        <span className={styles.current}>{current ? PRIVACY_LABEL[current] : 'não lida'}</span>
        {outcome ? <OutcomeNote outcome={outcome} /> : null}
      </span>
    </li>
  )
}

function OutcomeNote({ outcome }: { outcome: VisibilityOutcome }) {
  switch (outcome.state) {
    case 'ok':
      return (
        <span className={styles.ok}>
          mudou
          {outcome.result.destination_pending ? ' · post do Instagram pendente será cancelado' : ''}
        </span>
      )
    case 'unchanged':
      return <span className={styles.muted}>sem mudança</span>
    case 'skipped':
      return <span className={styles.muted}>não tentado (cota)</span>
    case 'failed':
      return (
        <span className={styles.failed}>
          {visibilityErrorMessage(outcome.error)}
          {isYoutubeReauthError(outcome.error) ? (
            <>
              {' '}
              <Link to="/channels" className={styles.link}>
                Abrir canais
              </Link>
            </>
          ) : null}
        </span>
      )
  }
}

function summaryToast(outcomes: readonly VisibilityOutcome[]): void {
  const count = (state: VisibilityOutcome['state']) => outcomes.filter((o) => o.state === state).length
  const ok = count('ok')
  const unchanged = count('unchanged')
  const failed = count('failed')
  const skipped = count('skipped')
  const parts = [
    ok > 0 ? countLabel(ok, 'mudou', 'mudaram') : null,
    unchanged > 0 ? countLabel(unchanged, 'já estava assim', 'já estavam assim') : null,
    failed > 0 ? countLabel(failed, 'falhou', 'falharam') : null,
    skipped > 0 ? `${countLabel(skipped, 'não tentado', 'não tentados')} (cota)` : null,
  ].filter(Boolean)
  const quota = outcomes.some((o) => o.state === 'failed' && isYoutubeQuotaError(o.error))
  const text = parts.join(' · ')
  if (failed > 0 || skipped > 0) {
    toast.error(text, quota ? { description: 'A cota do YouTube acabou; o resto fica para amanhã.' } : undefined)
  } else {
    toast.success(text)
  }
}
